import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./treatmentPlan.module.css";
import BackArrowButton from "../backArrowButton/backArrowButton";
import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import AddTreatmentPlan from "../addTreatmentPlan/addTreatmentPlan";
import UserInfo from "../userInfo/userInfo";
import {
  useDeleteAppointmentsProgramsMutation,
  useGetAppointmentsProgramsByUserIdMutation,
  useLazyGetAllMetaTablesQuery,
} from "../../services/api/utils.api";
import {
  appointmentsProgramsDataSelector,
  userAppointmentsProgramsSelector,
} from "../../services/selectors/utils.selectors";
import { TAppointmentsProgramsData } from "../../services/types/utils.types";
import Modal from "../modals/modal/Modal";
import DeleteConfirmation from "../modals/deleteConfirmation/deleteConfirmation";
import GeneralSuccessPopup from "../modals/generalSuccessPopup/generalSuccessPopup";
import ErrorPopup from "../modals/errorPopup/errorPopup";
import AppointmentsProgramSchedulePopup from "../modals/appointmentsProgramSchedulePopup/appointmentsProgramSchedulePopup";
import { IUserReport } from "../../services/types/users.types";
import { noDataLabel } from "../../utils/constants";
import { formatDateTime, getPrintDate } from "../../utils/utils";
import RestoreProgramButton from "../restoreProgramButton/restoreProgramButton";
import ListPagination from "../../components/listPagination/listPagination";
import ActionButtonTooltip from "../ActionButtonTooltip/actionButtonTooltip";

function TreatmentPlan({
  user,
  onClose,
  inModal,
}: {
  user: IUserReport;
  onClose: () => void;
  inModal?: boolean;
}) {
  const admin = useAppSelector(adminSelector);
  const appointmentsProgramsData = useAppSelector(
    appointmentsProgramsDataSelector
  );
  const userAppointmentsPrograms = useAppSelector(
    userAppointmentsProgramsSelector
  );

  const [openModal, setOpenModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [openModalSuccess, setOpenModalSuccess] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);
  const [programToEdit, setProgramToEdit] = useState<
    TAppointmentsProgramsData | undefined
  >(undefined);
  const [showAddOrEditProgramForm, setShowAddOrEditProgramForm] =
    useState(false);
  const [programToDelete, setProgramToDelete] = useState<
    TAppointmentsProgramsData | undefined
  >(undefined);

  const [limits, setLimits] = useState({ start: 1, end: 8 });

  const displayedPrograms = (Array.isArray(userAppointmentsPrograms) ? userAppointmentsPrograms : []).slice(
    limits.start - 1,
    limits.end
  );
  const [getAllMetaTables] = useLazyGetAllMetaTablesQuery();
  const [getAppointmentsPrograms] =
    useGetAppointmentsProgramsByUserIdMutation();
  const [deleteProgram] = useDeleteAppointmentsProgramsMutation();

  useEffect(() => {
    if (admin && user.Users.Guid) {
      getAllMetaTables(admin.companyId);
      getAppointmentsPrograms({
        companyId: admin.companyId,
        usersGuid: user.Users.Guid,
      });
    }
  }, [getAllMetaTables, admin, getAppointmentsPrograms, user]);

  const refreshPrograms = () => {
    user?.Users?.Guid &&
      getAppointmentsPrograms({
        companyId: admin.companyId,
        usersGuid: user.Users.Guid,
      });
  };

  return (
    <section className={`${styles.section} ${inModal ? styles.sectionInModal : ''}`}>
      <BackArrowButton
        text={t("PREVIOUS_SCREEN")}
        onClick={onClose}
        position={"center"}
      />
      <h3 className={`title`}>{t("TREATMENT_PLAN")}</h3>
      {user && (
        <div className={styles.userInfoContainer}>
          <UserInfo user={user} />
        </div>
      )}
      {user ? (
        showAddOrEditProgramForm ? (
          <AddTreatmentPlan
            onClose={() => {
              setShowAddOrEditProgramForm(false);
              setProgramToEdit(undefined);
            }}
            user={user}
            program={programToEdit}
          />
        ) : (
          <>
            <button
              type="button"
              className={`addBtn ${styles.addBtn}`}
              onClick={() => {
                setProgramToEdit(undefined);
                setShowAddOrEditProgramForm(true);
              }}
            >
              {t("ADD_NEW_TREATMENT")}
              <span className={`plus`}>+</span>
            </button>
            {Array.isArray(userAppointmentsPrograms) && userAppointmentsPrograms.length > 0 ? (
              <>
                <article className="section">
                  <h3 className="sectionTitle">{t("TREATMENT_PLAN")}</h3>
                  <div className={`list ${styles.list}`}>
                    <div
                      className={`listRow listRow_header ${styles.treatmentPlanList}`}
                    >
                      <p className="listCell">{t('NUMBER_1')}</p>
                      <p className="listCell">{t('NAME_2')}</p>
                      <p className="listCell">{t('NUMBER_OF_MEETINGS')}</p>
                      <p className="listCell">{t("SESSION_DURATION")}</p>
                      <p className="listCell">{t("COLUMN_FREQUENCY")}</p>
                      <p className="listCell">{t("SUPPORT_ROLE")}</p>
                      <p className="listCell">{t("COLUMN_STATUS")}</p>
                      <p className="listCell">{t('CREATED_BY_1')}</p>
                      <p className="listCell">{t("THERAPIST_NAME")}</p>
                      <p className="listCell">{t('DATE_OF_CREATION')}</p>
                      <p className="listCell">{t("COLUMN_OPTIONS")}</p>
                    </div>
                    {displayedPrograms.map((p, i) => {
                      const index = limits.start - 1 + i;
                      return (
                        <div
                          key={p.appointmentsProgram?.id}
                          className={`listRow ${styles.treatmentPlanList}  ${
                            index % 2 === 0 ? "colored" : ""
                          }`}
                        >
                          <p className="listCell">
                            {p.appointmentsProgram?.id}
                          </p>
                          <p className="listCell">
                            {p.appointmentsProgram.name}
                          </p>
                          <p className="listCell">
                            {p.appointmentsProgram.countOfAppointments}
                          </p>
                          <p className="listCell">
                            {appointmentsProgramsData.AppointmentsDurations.find(
                              (a) =>
                                a.Id ===
                                p.appointmentsProgram.appointmentsDurationsId
                            )?.Minutes || "-"}
                          </p>
                          <p className="listCell">
                            {appointmentsProgramsData.AppointmentsSequence.find(
                              (a) =>
                                a.Id ===
                                p.appointmentsProgram.appointmentsSequenceId
                            )?.label || "-"}
                          </p>
                          <p className="listCell">
                            {appointmentsProgramsData.Roles.find(
                              (r) => r.Id === p.appointmentsProgram.roleId
                            )?.label || p.appointmentsProgram.roleId}
                          </p>
                          <p className="listCell">
                            <span
                              className={`${
                                p.appointmentsProgram.statusId === 0 ||
                                p.appointmentsProgram.statusId === 8
                                  ? "status_inactive"
                                  : ""
                              }`}
                            >
                              {appointmentsProgramsData.AppointmentsProgramsStatus.find(
                                (s) => s.Id === p.appointmentsProgram.statusId
                              )?.label || p.appointmentsProgram.statusId}
                            </span>
                          </p>
                          <p className="listCell">
                            {p.createUserName || t(noDataLabel)}
                          </p>
                          <p className="listCell">
                            {p.therapistUserName || t(noDataLabel)}
                          </p>
                          <p className="listCell">
                            {p.appointmentsProgram.createDate
                              ? getPrintDate(
                                  new Date(p.appointmentsProgram.createDate)
                                )
                              : "-"}
                          </p>
                          <div className="listCell">
                            <ActionButtonTooltip
                              className="btn btn_type_edit"
                              onClick={() => {
                                setProgramToEdit(p);
                                setShowAddOrEditProgramForm(true);
                              }}
                              text={t('EDIT_PLAN')}
                            />
                            <ActionButtonTooltip
                              className="btn btn_type_schedule"
                              onClick={() => {
                                setProgramToEdit(p);
                                setShowScheduleModal(true);
                              }}
                              text={t("VIEW_PLAN_DATES")}
                              tooltipPosition="left"
                            />
                            {p?.appointmentsProgram?.statusId === 0 ? (
                              <RestoreProgramButton
                                program={p.appointmentsProgram}
                                openModalSuccess={() =>
                                  setOpenModalSuccess(true)
                                }
                                openModalError={() => setOpenModalError(true)}
                                refreshPrograms={refreshPrograms}
                              />
                            ) : (
                              <ActionButtonTooltip
                                className={`btn btn_type_delete ${
                                  p?.appointmentsProgram?.statusId !== 1
                                    ? "btn_type_delete_disabled"
                                    : ""
                                }`}
                                onClick={() => {
                                  if (p?.appointmentsProgram?.statusId === 1) {
                                    setProgramToDelete(p);
                                    setOpenModal(true);
                                  }
                                }}
                                text={
                                  p?.appointmentsProgram?.statusId !== 1
                                    ? t("PLAN_CONTAINS_SESSIONS_MESSAGE")
                                    : t("CANCEL_PLAN")
                                }
                                tooltipPosition="left"
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {Array.isArray(userAppointmentsPrograms) && userAppointmentsPrograms.length > 7 && (
                    <ListPagination
                      limits={limits}
                      total={userAppointmentsPrograms.length}
                      step={7}
                      setLimits={setLimits}
                    />
                  )}
                </article>
              </>
            ) : (
              <p className={`text`}>{t("NO_RESULTS_FOUND")}</p>
            )}
          </>
        )
      ) : (
        <p className={`title`}>{t("USER_NOT_FOUND")}</p>
      )}

      {/* מודאלים */}
      {openModal && programToDelete && (
        <Modal
          onClose={() => {
            setOpenModal(false);
            setProgramToDelete(undefined);
          }}
        >
          <DeleteConfirmation
            text={t('ARE_YOU_SURE_TO_DELETE_PROGRAM', { name: programToDelete?.appointmentsProgram?.name })}
            onDelete={() => {
              const { usersId, therapistsId, ...filteredAppointmentsProgram } =
                programToDelete.appointmentsProgram;
              deleteProgram({
                ...filteredAppointmentsProgram,
                updateUsersGuid: admin.guid,
                updateDate: formatDateTime(new Date()),
              })
                .then((res) => {
                  if (res && "data" in res) {
                    setOpenModalSuccess(true);
                    refreshPrograms();
                  }
                })
                .catch((err) => {
                  setOpenModalError(true);
                  console.log(err);
                });
            }}
            onClose={() => {
              setOpenModal(false);
              setProgramToDelete(undefined);
            }}
          />
        </Modal>
      )}
      {openModalSuccess && (
        <Modal onClose={() => setOpenModalSuccess(false)}>
          <GeneralSuccessPopup onClose={() => setOpenModalSuccess(false)} />
        </Modal>
      )}
      {openModalError && (
        <Modal onClose={() => setOpenModalError(false)}>
          <ErrorPopup onClose={() => setOpenModalError(false)} />
        </Modal>
      )}
      {showScheduleModal && programToEdit && (
        <Modal
          wide={true}
          onClose={() => {
            setShowScheduleModal(false);
            setProgramToEdit(undefined);
          }}
        >
          <AppointmentsProgramSchedulePopup program={programToEdit} />
        </Modal>
      )}
    </section>
  );
}
export default TreatmentPlan;
